import React, { useCallback, useEffect, useState } from "react";
import Modal from "../../components/Modal";
import Pager from "../../components/Pager";
import SecretBox from "../../components/SecretBox";
import { errorMessage } from "../../services";
import { getFranchisees } from "../../services/franchisees";
import { createOwner, deleteOwner, getOwnerStaff, getOwners, resetOwnerPassword, updateOwner } from "../../services/owners";
import { dateText } from "../../utils/format";
import { useDebounced } from "../../utils/hooks";
import DataTable from "../../components/DataTable";
import Loader, { Spinner } from "../../components/Loader";
import { FilterButton, FilterChips, FilterField, FilterSheet } from "../../components/FilterSheet";
import { startViewAs } from "../../utils/viewAs";

// Not logged in for 6 months or more: worth a look before renewing or deleting
const inactiveLong = (o) => {
  const cutoff = new Date();
  cutoff.setMonth(cutoff.getMonth() - 6);
  return new Date(o.lastActiveAt) < cutoff;
};

const STATUS_OPTIONS = [
  { value: "", label: "All" },
  { value: "active", label: "Active", tone: "green" },
  { value: "inactive", label: "Inactive", tone: "amber" },
  { value: "suspended", label: "Suspended", tone: "red" },
];

// Super Admin only. Deleting a customer removes their account, staff and every website (running ones too), so the
// name must be typed to confirm; the server checks it again.
function DeleteCustomerModal({ owner, notify, onClose, onDeleted }) {
  const [typed, setTyped] = useState("");
  const [busy, setBusy] = useState(false);
  const matches = typed.trim().toLowerCase() === owner.name.trim().toLowerCase();

  const remove = async (e) => {
    e.preventDefault();
    if (!matches) return;
    setBusy(true);
    try {
      const { data } = await deleteOwner(owner.id, typed);
      notify(data.message);
      onDeleted();
    } catch (err) {
      notify(errorMessage(err), "error");
      setBusy(false);
    }
  };

  return (
    <Modal eyebrow="DELETE CUSTOMER" title={`Delete ${owner.name}?`} onClose={onClose}>
      <form onSubmit={remove}>
        <div className="delete-site-warning">
          <span aria-hidden="true">⚠</span>
          <div>
            <b>This cannot be undone.</b>
            {owner.runningWebsites > 0 && (
              <p><b>{owner.runningWebsites} of their websites {owner.runningWebsites === 1 ? "is" : "are"} running now</b> (on a trial or a paid plan) and will go offline at once. Any time left on a paid plan is lost.</p>
            )}
            <ul>
              <li>Their account and {owner.staffCount ? `${owner.staffCount} staff login${owner.staffCount === 1 ? "" : "s"}` : "staff logins"} are deleted</li>
              <li>{owner.storesCount ? `Their ${owner.storesCount} website${owner.storesCount === 1 ? "" : "s"}, with products, orders, shoppers and images, are deleted` : "They have no websites"}</li>
            </ul>
            <p className="muted">Last logged in: {dateText(owner.lastActiveAt)}. Subscription payments are kept in Payments.</p>
          </div>
        </div>
        <label className="admin-field">Type <b>{owner.name}</b> to confirm
          <input value={typed} onChange={(e) => setTyped(e.target.value)} autoComplete="off" autoFocus />
        </label>
        <div className="product-modal-actions">
          <button type="button" className="outline-btn" onClick={onClose}>Cancel</button>
          <button className="danger-btn" disabled={!matches || busy}>{busy ? <><Spinner /> Deleting</> : "Delete customer"}</button>
        </div>
      </form>
    </Modal>
  );
}

// Customers = store owners. A Sub Admin only ever sees their own; the Super Admin sees everyone.
function CustomerModal({ owner, isSuper, franchises, onClose, onSaved, notify }) {
  const editing = Boolean(owner);
  const [form, setForm] = useState({
    name: owner ? owner.name : "",
    email: owner ? owner.email || "" : "",
    phone: owner ? owner.phone || "" : "",
    franchiseId: owner ? owner.franchiseId || "" : "",
  });
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (!form.email.trim() && !form.phone.trim()) return setError("Enter an email or a phone number.");
    setBusy(true);
    try {
      const payload = { name: form.name.trim(), email: form.email.trim(), phone: form.phone.trim() };
      if (isSuper) payload.franchiseId = form.franchiseId || null;
      if (editing) {
        await updateOwner(owner.id, payload);
        notify("Customer updated");
        onSaved(null);
      } else {
        if (!payload.franchiseId) delete payload.franchiseId;
        const { data } = await createOwner(payload);
        onSaved({ title: `Customer created: ${data.owner.name}`, login: data.owner.email || data.owner.phone, password: data.temporaryPassword });
      }
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal eyebrow="CUSTOMER" title={editing ? "Edit customer" : "Add customer"} onClose={onClose}
      description={editing ? undefined : "Creates a store owner account with a temporary password."}>
      <form onSubmit={submit}>
        {error && <div className="form-error">{error}</div>}
        <label className="admin-field">Full name<input required minLength={2} value={form.name} onChange={set("name")} /></label>
        <div className="form-row">
          <label className="admin-field">Email<input type="email" value={form.email} onChange={set("email")} /></label>
          <label className="admin-field">Mobile number<input value={form.phone} onChange={set("phone")} /></label>
        </div>
        {isSuper && (
          <label className="admin-field">Franchise
            <select value={form.franchiseId} onChange={set("franchiseId")}>
              <option value="">Ownezzy direct (no franchise)</option>
              {franchises.map((f) => <option key={f.id} value={f.id}>{f.name}</option>)}
            </select>
          </label>
        )}
        {editing && isSuper && <p className="muted">Changing the franchise also moves this customer’s websites.</p>}
        <div className="product-modal-actions">
          <button type="button" className="outline-btn" onClick={onClose}>Cancel</button>
          <button className="primary-btn" disabled={busy}>{busy ? <><Spinner /> Saving</> : editing ? "Save changes" : "Create customer"}</button>
        </div>
      </form>
    </Modal>
  );
}

// Super Admin: a customer's staff, each with "View as"
function OwnerStaffModal({ owner, onClose, notify }) {
  const [staff, setStaff] = useState(null);
  useEffect(() => {
    getOwnerStaff(owner.id).then(({ data }) => setStaff(data.staff)).catch((err) => { notify(errorMessage(err), "error"); setStaff([]); });
  }, [owner.id, notify]);
  return (
    <Modal eyebrow="STAFF" title={`${owner.name}'s staff`} onClose={onClose} description="View the admin panel exactly as one of them sees it.">
      {!staff && <Loader label="Loading staff" />}
      {staff && !staff.length && <p className="muted">No staff.</p>}
      <div className="owner-staff-list">
        {(staff || []).map((m) => (
          <div className="owner-staff-row" key={m.id}>
            <span className="staff-avatar" aria-hidden="true">{m.name.charAt(0).toUpperCase()}</span>
            <div>
              <b>{m.name}</b>
              <small>{m.email || m.phone} · {m.permissions.join(", ")}{m.status === "active" ? "" : " · paused"}</small>
            </div>
            {m.status === "active" && <button className="view-as-action" onClick={() => startViewAs(m.id).catch((err) => notify(errorMessage(err), "error"))}>👁 View as</button>}
          </div>
        ))}
      </div>
    </Modal>
  );
}

export default function CustomersTab({ user, notify }) {
  const isSuper = user.role === "superadmin";
  const [owners, setOwners] = useState([]);
  const [meta, setMeta] = useState({ total: 0, page: 1, pages: 1 });
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("");
  const [pageNumber, setPageNumber] = useState(1);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [modal, setModal] = useState(null); // 'new' or an owner being edited
  const [franchises, setFranchises] = useState([]);
  const [secret, setSecret] = useState(null);
  const [deleting, setDeleting] = useState(null); // the customer being deleted (Super Admin)
  const [staffOf, setStaffOf] = useState(null); // a customer whose staff list is open
  const [franchiseId, setFranchiseId] = useState(""); // Super Admin: only one franchise's customers
  const [filtersOpen, setFiltersOpen] = useState(false);
  const debouncedSearch = useDebounced(search);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await getOwners({ search: debouncedSearch, status, franchiseId: franchiseId || undefined, page: pageNumber, limit: 20 });
      setOwners(data.owners);
      setMeta({ total: data.total, page: data.page, pages: data.pages });
      setError("");
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setLoading(false);
    }
  }, [debouncedSearch, status, franchiseId, pageNumber]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    if (isSuper) getFranchisees({ limit: 100 }).then(({ data }) => setFranchises(data.franchisees)).catch(() => setFranchises([]));
  }, [isSuper]);

  const activeFilters = [status, franchiseId].filter(Boolean).length;
  const resetFilters = () => { setStatus(""); setFranchiseId(""); setPageNumber(1); };
  const franchiseSelect = (
    <select value={franchiseId} onChange={(e) => { setFranchiseId(e.target.value); setPageNumber(1); }} aria-label="Filter by franchise">
      <option value="">All franchises</option>
      {franchises.map((f) => <option key={f.id} value={f.id}>{f.name}</option>)}
    </select>
  );

  const toggleStatus = async (owner) => {
    try {
      await updateOwner(owner.id, { status: owner.status === "active" ? "inactive" : "active" });
      notify(`${owner.name} ${owner.status === "active" ? "deactivated" : "activated"}`);
      load();
    } catch (err) {
      notify(errorMessage(err), "error");
    }
  };

  const resetPassword = async (owner) => {
    try {
      const { data } = await resetOwnerPassword(owner.id);
      setSecret({ title: `Password reset for ${owner.name}`, login: owner.email || owner.phone, password: data.temporaryPassword });
    } catch (err) {
      notify(errorMessage(err), "error");
    }
  };

  return (
    <>
      <div className="page-heading">
        <div>
          <p className="eyebrow">CUSTOMER ACCOUNTS</p>
          <h1>Customers</h1>
          <p className="subheading">Business owners who buy websites{isSuper ? "" : " from your franchise"}.</p>
        </div>
        <button className="primary-btn" onClick={() => setModal("new")}>＋ Add customer</button>
      </div>

      {secret && <SecretBox {...secret} onDone={() => setSecret(null)} />}

      <div className="panel table-panel">
        <div className="table-toolbar">
          <input value={search} onChange={(e) => { setSearch(e.target.value); setPageNumber(1); }} placeholder="Search name, email or phone…" />
          <FilterButton count={activeFilters} onClick={() => setFiltersOpen(true)} />
          <div className="toolbar-inline-filters">
            <select value={status} onChange={(e) => { setStatus(e.target.value); setPageNumber(1); }} aria-label="Filter by status">
              <option value="">All statuses</option><option value="active">Active</option><option value="inactive">Inactive</option><option value="suspended">Suspended</option>
            </select>
            {isSuper && franchises.length > 0 && franchiseSelect}
          </div>
          <span>{meta.total} customers</span>
        </div>
        <div className="toolbar-count">{meta.total} customers</div>
        {error && <div className="admin-error">{error}</div>}
        <DataTable>
            <thead>
              <tr><th>CUSTOMER</th><th>EMAIL</th><th>WEBSITES</th>{isSuper && <th>FRANCHISE</th>}<th>LAST ACTIVE</th><th>STATUS</th><th>ACTIONS</th></tr>
            </thead>
            <tbody>
              {owners.map((o) => (
                <tr key={o.id}>
                  <td><div className="site-name-cell"><span className="site-mark">{o.name.slice(0, 1)}</span><span><b>{o.name}</b>{o.phone && <small>{o.phone}</small>}</span></div></td>
                  <td>{o.email ? <a className="cell-email" href={`mailto:${o.email}`}>{o.email}</a> : <span className="cell-sub">No email</span>}</td>
                  <td>{o.storesCount}</td>
                  {isSuper && <td>{o.franchiseName || "Ownezzy direct"}</td>}
                  <td>{dateText(o.lastActiveAt)}{inactiveLong(o) && <small className="cell-sub due-text">Inactive 6+ months</small>}</td>
                  <td><span className={`status-pill ${o.status === "active" ? "status-on" : "status-off"}`}><i />{o.status}</span></td>
                  <td>
                    <div className="row-actions">
                      <button className="edit-action" onClick={() => setModal(o)}>Edit</button>
                      <button className="toggle-action" onClick={() => toggleStatus(o)}>{o.status === "active" ? "Deactivate" : "Activate"}</button>
                      <button className="toggle-action" onClick={() => resetPassword(o)}>Reset password</button>
                      {isSuper && o.staffCount > 0 && <button className="toggle-action" onClick={() => setStaffOf(o)}>👥 Staff ({o.staffCount})</button>}
                      {o.deletable && <button className="delete-action" onClick={() => setDeleting(o)}>Delete</button>}
                      {o.demoStores > 0 && <span className="cell-sub" title="This account runs Ownezzy's demo stores and cannot be deleted">Demo stores</span>}
                      {isSuper && o.status === "active" && <button className="view-as-action" onClick={() => startViewAs(o.id).catch((err) => notify(errorMessage(err), "error"))}>👁 View as</button>}
                    </div>
                  </td>
                </tr>
              ))}
              {!owners.length && <tr><td colSpan={isSuper ? 7 : 6} className="empty-row">{loading ? <Loader label="Loading customers" /> : "No customers yet."}</td></tr>}
            </tbody>
        </DataTable>
        <Pager page={meta.page} pages={meta.pages} total={meta.total} noun="customers" onPage={setPageNumber} />
      </div>

      {staffOf && <OwnerStaffModal owner={staffOf} notify={notify} onClose={() => setStaffOf(null)} />}
      {deleting && <DeleteCustomerModal owner={deleting} notify={notify} onClose={() => setDeleting(null)} onDeleted={() => { setDeleting(null); load(); }} />}
      {filtersOpen && (
        <FilterSheet title="Filter customers" resultLabel={`Show ${meta.total} customer${meta.total === 1 ? "" : "s"}`} onClose={() => setFiltersOpen(false)} onReset={resetFilters}>
          <FilterChips label="Status" value={status} onChange={(v) => { setStatus(v); setPageNumber(1); }} options={STATUS_OPTIONS} />
          {isSuper && franchises.length > 0 && <FilterField label="Franchise">{franchiseSelect}</FilterField>}
        </FilterSheet>
      )}
      {modal && (
        <CustomerModal owner={modal === "new" ? null : modal} isSuper={isSuper} franchises={franchises} notify={notify}
          onClose={() => setModal(null)}
          onSaved={(created) => { setModal(null); if (created) setSecret(created); load(); }} />
      )}
    </>
  );
}
